/**
 * Snapshotter registry — undo/redo'nun "neyi yakalayıp geri yükleyeceğini"
 * tanımlayan genişleme noktası.
 */
import { Color, Material, Mesh, Object3D } from "three";

export interface Snapshotter<T = unknown> {
  id: string;
  match(subject: unknown): boolean;
  capture(subject: unknown): T;
  restore(subject: unknown, snapshot: T, dir: "undo" | "redo"): void;
  equal?(a: T, b: T): boolean;
}

const _snapshotters: Snapshotter[] = [];

export function registerSnapshotter<T>(s: Snapshotter<T>): void {
  _snapshotters.push(s as Snapshotter);
}

export function getSnapshotters(): readonly Snapshotter[] {
  return _snapshotters;
}

interface CapturedField {
  snapshotter: Snapshotter;
  value: unknown;
}

export interface CapturedSubject {
  subject: unknown;
  fields: CapturedField[];
}

export function captureSubject(subject: unknown): CapturedSubject {
  const fields: CapturedField[] = [];
  for (const s of _snapshotters) {
    if (s.match(subject)) fields.push({ snapshotter: s, value: s.capture(subject) });
  }
  return { subject, fields };
}

export function captureAll(subjects: Iterable<unknown>): CapturedSubject[] {
  return [...subjects].map(captureSubject);
}

export function fieldsEqual(s: Snapshotter, a: unknown, b: unknown): boolean {
  return s.equal ? s.equal(a, b) : a === b;
}

const EPS = 1e-5;
const triEq = (a: number[], b: number[]) =>
  a.every((v, i) => Math.abs(v - b[i]) <= EPS);

function isObject3D(o: unknown): o is Object3D {
  return o instanceof Object3D;
}

interface TransformSnap {
  p: [number, number, number];
  q: [number, number, number, number];
  s: [number, number, number];
}
registerSnapshotter<TransformSnap>({
  id: "transform",
  match: isObject3D,
  capture: (o) => {
    const obj = o as Object3D;
    return {
      p: [obj.position.x, obj.position.y, obj.position.z],
      q: [obj.quaternion.x, obj.quaternion.y, obj.quaternion.z, obj.quaternion.w],
      s: [obj.scale.x, obj.scale.y, obj.scale.z],
    };
  },
  restore: (o, snap) => {
    const obj = o as Object3D;
    obj.position.set(snap.p[0], snap.p[1], snap.p[2]);
    obj.quaternion.set(snap.q[0], snap.q[1], snap.q[2], snap.q[3]);
    obj.scale.set(snap.s[0], snap.s[1], snap.s[2]);
    obj.updateMatrix();
  },
  equal: (a, b) => triEq(a.p, b.p) && triEq(a.q, b.q) && triEq(a.s, b.s),
});

interface CommonSnap {
  name: string;
  visible: boolean;
}
registerSnapshotter<CommonSnap>({
  id: "common",
  match: isObject3D,
  capture: (o) => {
    const obj = o as Object3D;
    return { name: obj.name, visible: obj.visible };
  },
  restore: (o, snap) => {
    const obj = o as Object3D;
    obj.name = snap.name;
    obj.visible = snap.visible;
  },
  equal: (a, b) => a.name === b.name && a.visible === b.visible,
});

interface GraphSnap {
  parent: Object3D | null;
  index: number;
}
registerSnapshotter<GraphSnap>({
  id: "graph",
  match: isObject3D,
  capture: (o) => {
    const obj = o as Object3D;
    return {
      parent: obj.parent,
      index: obj.parent ? obj.parent.children.indexOf(obj) : -1,
    };
  },
  restore: (o, snap) => {
    const obj = o as Object3D;
    const cur = obj.parent;
    if (!snap.parent) {
      cur?.remove(obj);
      return;
    }
    if (cur !== snap.parent) {
      cur?.remove(obj);
      snap.parent.add(obj);
    } else if (cur && cur.children.indexOf(obj) === -1) {
      snap.parent.add(obj);
    }
    const arr = snap.parent.children;
    const now = arr.indexOf(obj);
    const idx = Math.max(0, Math.min(snap.index, arr.length - 1));
    if (now !== -1 && now !== idx) {
      arr.splice(now, 1);
      arr.splice(idx, 0, obj);
    }
  },
  equal: (a, b) => a.parent === b.parent && a.index === b.index,
});

interface MatFields {
  color?: number;
  emissive?: number;
  opacity?: number;
  transparent?: boolean;
  metalness?: number;
  roughness?: number;
  side?: number;
  visible?: boolean;
}
type MatLike = Material & {
  color?: Color;
  emissive?: Color;
  metalness?: number;
  roughness?: number;
};

function captureMat(m: MatLike): MatFields {
  return {
    color: m.color?.getHex(),
    emissive: m.emissive?.getHex(),
    opacity: m.opacity,
    transparent: m.transparent,
    metalness: m.metalness,
    roughness: m.roughness,
    side: m.side,
    visible: m.visible,
  };
}
function applyMat(m: MatLike, f: MatFields): void {
  if (f.color !== undefined && m.color) m.color.setHex(f.color);
  if (f.emissive !== undefined && m.emissive) m.emissive.setHex(f.emissive);
  if (f.opacity !== undefined) m.opacity = f.opacity;
  if (f.transparent !== undefined) m.transparent = f.transparent;
  if (f.metalness !== undefined) m.metalness = f.metalness;
  if (f.roughness !== undefined) m.roughness = f.roughness;
  if (f.side !== undefined) m.side = f.side as Material["side"];
  if (f.visible !== undefined) m.visible = f.visible;
}
const matEq = (a: MatFields, b: MatFields) =>
  (Object.keys(a) as (keyof MatFields)[]).every((k) => a[k] === b[k]);

registerSnapshotter<MatFields[]>({
  id: "material",
  match: (o) => isObject3D(o) && !!(o as Mesh).material,
  capture: (o) => {
    const mat = (o as Mesh).material;
    return (Array.isArray(mat) ? mat : [mat]).map((m) => captureMat(m as MatLike));
  },
  restore: (o, snaps) => {
    const mat = (o as Mesh).material;
    const arr = Array.isArray(mat) ? mat : [mat];
    arr.forEach((m, i) => snaps[i] && applyMat(m as MatLike, snaps[i]));
  },
  equal: (a, b) => a.length === b.length && a.every((f, i) => matEq(f, b[i])),
});

export const builtinSnapshottersLoaded = true;
